import { useDepartmentText } from 'model/DepartmentTerminology';
import { Badge, Stack, Tooltip } from '@mantine/core';
import { OrganizationRow } from 'components/tables/data/organization/types';
import P2Regular from 'components/typography/P2Regular';
import { Link } from 'react-router-dom';
import { DEPARTMENTS_PAGE_PATH } from 'routes/paths';

const TOOLTIP_DEPARTMENT_LIMIT = 15;

/** Formats an object count with its Slovak grammatical form. */
function getDepartmentCountLabel(
  departmentCount: number,
  departmentText: (objectText: string, departmentText: string) => string
) {
  if (departmentCount === 1) {
    return `${departmentCount} ${departmentText('Položka úrovne', 'Stredisko')}`;
  }

  if (departmentCount >= 2 && departmentCount <= 4) {
    return `${departmentCount} ${departmentText('Položky úrovne', 'Strediská')}`;
  }

  return `${departmentCount} ${departmentText('Položiek úrovne', 'Stredísk')}`;
}

/** Hierarchical department rows shown by the organization object-count tooltip. */
function DepartmentsTooltip({ departments }: Pick<OrganizationRow, 'departments'>) {
  const visibleDepartments = departments.slice(0, TOOLTIP_DEPARTMENT_LIMIT);
  const remainingCount = departments.length - visibleDepartments.length;

  return (
    <Stack spacing={4}>
      {visibleDepartments.map(({ departmentId, departmentName, breadcrumbs }) => (
        <P2Regular key={departmentId} color="gray.8" style={{ paddingLeft: Math.max(0, breadcrumbs.length - 1) * 16 }}>
          {departmentName}
        </P2Regular>
      ))}
      {remainingCount > 0 && <P2Regular color="gray.6">(+{remainingCount} ďalších)</P2Regular>}
    </Stack>
  );
}

/**
 * Column for the departments of an organization.
 */
export default function DepartmentsColumn({ data }: { data: OrganizationRow }) {
  const departmentText = useDepartmentText();
  const { departmentCount, departments, organizationId, organizationName } = data;
  const departmentCountLabel = getDepartmentCountLabel(departmentCount, departmentText);
  const badge = (
    <Badge
      component={Link}
      to={`${DEPARTMENTS_PAGE_PATH.original}?organizationId=${organizationId}`}
      aria-label={`${departmentText(
        'Zobraziť položky úrovne organizácie',
        'Zobraziť strediská organizácie'
      )} ${organizationName}: ${departmentCountLabel}`}
      sx={{ cursor: 'pointer', textDecoration: 'none' }}
    >
      {departmentCountLabel}
    </Badge>
  );

  if (departments.length === 0) {
    return badge;
  }

  return (
    <Tooltip bg="gray.2" withArrow multiline withinPortal label={<DepartmentsTooltip departments={departments} />}>
      {badge}
    </Tooltip>
  );
}
